import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip
} from '@mui/material';
import { FaEnvelope, FaUserPlus, FaUser, FaTimes, FaCheck, FaSpinner } from 'react-icons/fa';

const TeamInviteDialog = ({ 
  open, 
  onClose, 
  onInvite, 
  loading = false,
  error = null,
  success = null
}) => {
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    userRole: 'member',
    message: ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.firstName) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName) {
      newErrors.lastName = 'Last name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      await onInvite(formData);
      // Reset form on success
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        userRole: 'member',
        message: ''
      });
      setErrors({});
    } catch (error) {
      console.error('Error inviting team member:', error);
    }
  };

  const handleClose = () => {
    setFormData({
      email: '',
      firstName: '',
      lastName: '',
      userRole: 'member',
      message: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <FaUserPlus style={{ color: '#3b9efc' }} />
        <Typography variant="h6" fontWeight={600}>
          Invite Team Member
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Typography variant="body2" color="text.secondary" mb={3}>
          Send an invitation to join your team. The member will receive an email with instructions to join.
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            label="First Name"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            error={!!errors.firstName}
            helperText={errors.firstName}
            fullWidth
            size="small"
            required
          />
          
          <TextField
            label="Last Name"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            error={!!errors.lastName}
            helperText={errors.lastName}
            fullWidth
            size="small"
            required
          />
        </Box>
        
        <TextField
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          error={!!errors.email}
          helperText={errors.email}
          fullWidth
          size="small"
          required
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: <FaEnvelope style={{ marginRight: 8, color: '#9ca3af' }} />
          }}
        />
        
        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
          <InputLabel>Role</InputLabel>
          <Select
            value={formData.userRole}
            onChange={(e) => handleInputChange('userRole', e.target.value)}
            label="Role"
          >
            <MenuItem value="member">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FaUser size={12} />
                <Typography>Member</Typography>
                <Chip 
                  label="Limited access" 
                  size="small" 
                  sx={{ ml: 'auto', fontSize: 10, height: 16 }}
                />
              </Box>
            </MenuItem>
            <MenuItem value="admin">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FaUser size={12} />
                <Typography>Admin</Typography>
                <Chip 
                  label="Full access" 
                  size="small" 
                  color="primary"
                  sx={{ ml: 'auto', fontSize: 10, height: 16 }}
                />
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
        
        <TextField
          label="Personal Message (Optional)"
          value={formData.message}
          onChange={(e) => handleInputChange('message', e.target.value)}
          multiline
          rows={3}
          fullWidth
          size="small"
          placeholder="Add a personal message to the invitation email..."
          sx={{ mb: 2 }}
        />
        
        <Box sx={{ 
          p: 2, 
          bgcolor: '#f8fafc', 
          borderRadius: 2, 
          border: '1px solid #e2e8f0'
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            What happens next?
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <FaCheck size={10} style={{ color: '#10b981' }} />
            <Typography variant="caption" color="text.secondary">
              Member can accept and join your team
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FaCheck size={10} style={{ color: '#10b981' }} />
            <Typography variant="caption" color="text.secondary">
              You'll be notified when they join
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          startIcon={<FaTimes />}
          sx={{ color: '#6b7280' }}
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleSubmit}
          disabled={loading || !formData.email || !formData.firstName || !formData.lastName}
          variant="contained"
          startIcon={loading ? <FaSpinner size={16} /> : <FaUserPlus />}
          sx={{ 
            bgcolor: '#3b9efc',
            '&:hover': { bgcolor: '#2563eb' }
          }}
        >
          {loading ? 'Sending Invitation...' : 'Send Invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamInviteDialog; 